import { AntDesign, FontAwesome5 } from "@expo/vector-icons";
import { BlurView } from "expo-blur";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Modal, View, Text, Pressable } from "react-native";
import { useDispatch } from "react-redux";

import Title from "@/components/Title";
import Button from "@/components/button/Button";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import i18n from "@/locales/i18n";
import { Web3Transfer } from "@/screens/home/transaction/Amount";
import { sendRequest } from "@/utils/ApiUtil";
import { toastInstance } from "@/utils/ToastConfig";
import { truncateMiddle } from "@/utils/text/FormatTextUtils";
import { checkAsset, checkIcon } from "@/utils/transactions/ExchangeCurrencies";
import {
  useSendTransaction,
  createTronTransaction,
} from "@/utils/web3/Transactions";

interface ReviewModalProps {
  visible: boolean;
  amountToSend: number;
  rail: string;
  onClose: () => void;
  onClick?: (state: boolean) => void;
  web3TransferData: Web3Transfer;
  isUsingWallet: boolean;
}

export default function ReviewModal(props: ReviewModalProps) {
  const [currentAccountData, setCurrentAccountData] = useState<any>({});
  const [waitingApproval, setWaitingApproval] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalValues = useAppSelector((state: any) => state);
  const { sendTransaction, error } = useSendTransaction();

  const currentCounterparty = globalValues.currentCounterpartySelected;
  const counterPartyAssetType =
    currentCounterparty?.walletInformation?.assetTypeId ||
    currentCounterparty?.accountInformation?.assetTypeId;

  const transactionFee = props.web3TransferData?.destinationAddress
    ? props.amountToSend * props.web3TransferData?.fee
    : getTransactionTypeFee(props.rail);

  const { handleSubmit, formState } = useForm<FormData>({
    mode: "onSubmit",
  });
  const { isSubmitting } = formState;

  useEffect(() => {
    const accountsData = globalValues.accountsData;

    if (accountsData && globalValues.currentAccountId) {
      const accountWithId = accountsData.find(
        (account: any) => account.id === globalValues.currentAccountId
      );
      if (accountWithId) {
        setCurrentAccountData(accountWithId);
      }
    }
  }, [globalValues.accountsData, globalValues.currentAccountId]);

  function getTransactionTypeFee(rail: string) {
    switch (rail) {
      case "SWIFT":
        return currentAccountData.feeStructure?.swift ?? "$0.00";
      case "FEDWIRE":
        return currentAccountData.feeStructure?.domestic ?? "$0.00";
      case "CRYPTO":
        return currentAccountData.feeStructure?.crypto ?? "$0.00";
      default:
        return "$0.00";
    }
  }

  const handleClick = async () => {
    const withdrawalId = globalValues.withdrawalId;
    try {
      const request = await sendRequest(
        "post",
        "/withdrawal/accept",
        { id: withdrawalId },
        true,
        true
      );
      if (request.status === "ACCEPTED") {
        props.onClose();
        dispatch(setForm({ transactionResponse: "SUCCESS" }));
        dispatch(setForm({ transactionModalVisible: true }));
        dispatch(setForm({ transactionsHasChanges: true }));
        router.push("/home");
      }
    } catch (error: any) {
      if (
        error.response.data.name === "TimeoutError" ||
        error.response.status === 504 ||
        error.status === 504
      ) {
        props.onClose();
        dispatch(setForm({ transactionResponse: "TIMEOUT" }));
        dispatch(setForm({ transactionModalVisible: true }));
        dispatch(setForm({ transactionsHasChanges: true }));
        router.push("/home");
      } else {
        props.onClose();
        dispatch(setForm({ transactionResponse: "FAILED" }));
        dispatch(setForm({ transactionModalVisible: true }));
        router.push("/home");
      }
    }
  };

  const handleConfirmationWithWallet = async () => {
    setWaitingApproval(true);
    try {
      if (props.web3TransferData.isTronWallet) {
        await createTronTransaction(
          props.web3TransferData.walletAddress,
          props.web3TransferData.destinationAddress,
          props.web3TransferData.currency,
          props.amountToSend,
          props.web3TransferData.memo
        );
      } else {
        if (!error) {
          await sendTransaction(
            props.web3TransferData.walletAddress,
            props.web3TransferData.destinationAddress,
            props.web3TransferData.rail,
            props.web3TransferData.currency,
            props.amountToSend
          );
        }
      }
      props.onClose();
      dispatch(setForm({ transactionResponse: "SUCCESS" }));
      dispatch(setForm({ transactionModalVisible: true }));
      router.push("/home");
    } catch (error) {
      console.error("Transaction failed", error);
      toastInstance("error", t("home/amount:sendwithwallet.error"));
      props.onClose();
    } finally {
      setWaitingApproval(false);
    }
  };

  return (
    <Modal visible={props.visible} transparent animationType="fade">
      <BlurView intensity={20} className="flex-1 justify-center items-center">
        <View className="bg-white1 border border-gray rounded-lg w-full h-full p-6 items-center justify-center relative shadow lg:w-112 lg:h-136">
          <Title text={t("common:modal.reviewtransaction.title")} />
          <Pressable className="absolute right-3 top-3" onPress={props.onClose}>
            <AntDesign name="close" size={24} color="black" />
          </Pressable>
          <View className="flex-grow w-full items-center justify-center">
            <View className="flex-col border-b border-darkergray items-center justify-between w-full p-4">
              <View className="flex-row items-center justify-center my-2">
                {props.web3TransferData?.currency
                  ? checkIcon(props.web3TransferData?.currency.toUpperCase())
                  : checkIcon(counterPartyAssetType)}
                <Text className="text-base font-medium mx-2">
                  {props.web3TransferData?.currency
                    ? checkAsset(props.web3TransferData?.currency.toUpperCase())
                    : checkAsset(counterPartyAssetType)}
                </Text>
              </View>
              {props.isUsingWallet && (
                <View className="flex-row w-full items-center justify-center gap-2 my-1">
                  <FontAwesome5
                    name="bitcoin"
                    size={16}
                    className="text-purple1"
                  />
                  <Text className="text-xs text-center text-purple1 font-medium">
                    {t("home/modals/reviewmodal:transferwithexternalwallet")}
                  </Text>
                </View>
              )}
              <View className="w-full">
                <View className="flex-row my-1 justify-between">
                  <Text className="text-base font-light">
                    {t("home/amount:input.transactionvalue")}:
                  </Text>
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="text-base"
                  >
                    {`${
                      globalValues.rail !== "CRYPTO" && !props.isUsingWallet
                        ? new Intl.NumberFormat(i18n.language, {
                            style: "currency",
                            currency: "USD",
                          }).format(props.amountToSend)
                        : props.amountToSend
                    }`}
                  </Text>
                </View>
                <View className="flex-row my-1 justify-between">
                  <Text className="text-base font-light">
                    {t("home/amount:input.fee")}:
                  </Text>
                  <Text className="text-base">{transactionFee}</Text>
                </View>
              </View>
            </View>
            <View className="border-b border-darkergray items-start w-full p-4">
              <View className="flex-row w-full justify-between my-1">
                <Text className="text-base font-light">
                  {t("home/amount:input.holdername")}:
                </Text>
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  className="text-base"
                >{`${currentCounterparty?.profile?.name ?? "-"}`}</Text>
              </View>
              {currentCounterparty?.accountInformation && (
                <>
                  <View className="flex-row w-full justify-between my-1">
                    {globalValues.isIbanAccount ? (
                      <Text className="text-base font-light">
                        {t("home/amount:input.iban")}:
                      </Text>
                    ) : (
                      <Text className="text-base font-light">
                        {t("home/amount:input.accountnumber")}:
                      </Text>
                    )}
                    <Text className="text-base">
                      {(currentCounterparty?.accountInformation?.iban ||
                        currentCounterparty?.accountInformation?.clabe ||
                        currentCounterparty?.accountInformation
                          ?.accountNumber) ??
                        "-"}
                    </Text>
                  </View>
                  <View className="flex-row w-full justify-between my-1">
                    {globalValues.rail === "SWIFT" ? (
                      <Text className="text-base font-light">
                        {t("home/amount:input.swiftbic")}:
                      </Text>
                    ) : (
                      <Text className="text-base font-light">
                        {t("home/amount:input.routingnumber")}:
                      </Text>
                    )}
                    <Text className="text-base">
                      {(currentCounterparty?.accountInformation?.swiftBic ||
                        currentCounterparty?.accountInformation
                          ?.routingNumber) ??
                        "-"}
                    </Text>
                  </View>
                </>
              )}
              {currentCounterparty?.walletInformation?.assetTypeId && (
                <View className="flex-row w-full justify-between my-1">
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="text-base font-light"
                  >
                    {t("home/amount:input.assettypeid")}:
                  </Text>
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="text-base"
                  >
                    {currentCounterparty?.walletInformation?.assetTypeId ?? "-"}
                  </Text>
                </View>
              )}
              {currentCounterparty?.walletInformation?.blockchainAddress && (
                <View className="flex-row w-full justify-between my-1">
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="text-base font-light"
                  >
                    {t("home/amount:input.walletaddress")}:
                  </Text>
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="text-base"
                  >
                    {currentCounterparty?.walletInformation?.blockchainAddress
                      ? truncateMiddle(
                          currentCounterparty?.walletInformation
                            ?.blockchainAddress
                        )
                      : "-"}
                  </Text>
                </View>
              )}
              <View className="flex-row w-full justify-between my-1">
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  className="text-base font-light"
                >
                  {t("home/amount:input.bankname")}:
                </Text>
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  className="text-base"
                >
                  {(currentCounterparty?.accountInformation?.institutionName ||
                    currentCounterparty?.walletInformation?.institutionName) ??
                    "-"}
                </Text>
              </View>
              {(currentCounterparty?.accountInformation?.institutionAddress ||
                currentCounterparty?.walletInformation?.institutionAddress) && (
                <View className="flex-row w-full justify-between my-1">
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="text-base font-light"
                  >
                    {t("home/amount:input.bankcountry")}:
                  </Text>
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="text-base"
                  >
                    {(currentCounterparty?.accountInformation
                      ?.institutionAddress?.countryCode ||
                      currentCounterparty?.walletInformation?.institutionAddress
                        ?.countryCode) ??
                      "-"}
                  </Text>
                </View>
              )}
            </View>
          </View>
          <Button
            label={
              props.isUsingWallet
                ? t("common:button.approveinwallet")
                : t("common:button.confirmtransaction")
            }
            isLoading={isSubmitting || waitingApproval}
            {...(props.isUsingWallet && {
              onClick: () => handleConfirmationWithWallet(),
            })}
            {...(!props.isUsingWallet && {
              onClick: handleSubmit(handleClick),
            })}
          />
        </View>
      </BlurView>
    </Modal>
  );
}
